import Phaser from 'phaser';
import { FpsMonitor } from '../utils/FpsMonitor';
import { GameSettings } from '../utils/GameSettings';

/**
 * Configuration interface for UI components
 */
export interface UIConfig {
  // Container settings
  containerDepth?: number;
  fontFamily?: string;
  
  // Progress bar settings
  progressBarWidth?: number;
  progressBarHeight?: number;
  progressBarColor?: number;
  progressBarBgColor?: number;
  
  // Button settings
  buttonBgColor?: number;
  buttonTextColor?: string;
  buttonHoverColor?: number;
  
  // Info panel settings
  infoPanelColor?: number;
  infoPanelAlpha?: number;
  infoPanelWidth?: number;
  infoPanelHeight?: number;
}

// Component registration interface for UI updates
export interface UIComponent {
  id: string;
  update: (data: any, delta?: number) => void;
}

/**
 * Default UI configuration
 */
const DEFAULT_UI_CONFIG: UIConfig = {
  containerDepth: 1000,
  fontFamily: 'Arial',
  
  progressBarWidth: 200,
  progressBarHeight: 15,
  progressBarColor: 0x4CAF50,
  progressBarBgColor: 0xffffff,
  
  buttonBgColor: 0x4CAF50,
  buttonTextColor: '#ffffff',
  buttonHoverColor: 0x3b9c3f,
  
  infoPanelColor: 0x000000,
  infoPanelAlpha: 0.7,
  infoPanelWidth: 600,
  infoPanelHeight: 80
};

/**
 * A reusable UI system that can be shared across different scenes
 */
export class GameUISystem {
  private scene: Phaser.Scene;
  private config: UIConfig;
  private uiContainer: Phaser.GameObjects.Container;
  private uiCamera: Phaser.Cameras.Scene2D.Camera | null = null;
  private fpsMonitor: FpsMonitor | null = null;
  private settingsManager: GameSettings;
  
  // Track common UI elements
  private progressBars: Map<string, {
    container: Phaser.GameObjects.Container;
    bar: Phaser.GameObjects.Rectangle;
    fill: Phaser.GameObjects.Rectangle;
    text?: Phaser.GameObjects.Text;
  }> = new Map();
  
  private buttons: Map<string, Phaser.GameObjects.Container> = new Map();
  private textElements: Map<string, Phaser.GameObjects.Text> = new Map();
  private infoPanel: Phaser.GameObjects.Container | null = null;
  private successPanel: Phaser.GameObjects.Container | null = null;
  private fullscreenButton: Phaser.GameObjects.Image | null = null;
  
  // Add registered components that need to be updated
  private registeredComponents: Map<string, UIComponent> = new Map();
  
  // Add resize handling flag
  private _handlingResize: boolean = false;
  
  constructor(scene: Phaser.Scene, config?: Partial<UIConfig>) {
    this.scene = scene;
    this.config = { ...DEFAULT_UI_CONFIG, ...config };
    this.settingsManager = GameSettings.getInstance();
    
    // Create or use existing UI container
    if ((this.scene as any).uiContainer) {
      this.uiContainer = (this.scene as any).uiContainer;
    } else {
      this.uiContainer = this.scene.add.container(0, 0);
      this.uiContainer.setDepth(this.config.containerDepth || 1000);
      
      // Store reference in scene for easier access
      (this.scene as any).uiContainer = this.uiContainer;
    }

    // Set up UI camera and resize listener
    this.setupUICamera();
    this.scene.scale.on('resize', this.handleResize, this);
  }

  /**
   * Sets up the UI camera that doesn't move or zoom
   */
  public setupUICamera(): void {
    // Check if UI camera already exists in the scene
    const existingUICam = this.scene.cameras.getCamera('uiCam');
    if (existingUICam) {
      this.uiCamera = existingUICam;
      console.log('Using existing UI camera');
      return;
    }

    // Create a separate UI camera that doesn't move or zoom
    this.uiCamera = this.scene.cameras.add(0, 0, this.scene.scale.width, this.scene.scale.height);
    this.uiCamera.setName('uiCam');
    this.uiCamera.setScroll(0, 0);
    this.uiCamera.transparent = true;
    
    // Ensure main camera ignores UI container
    this.scene.cameras.main.ignore(this.uiContainer);
    
    console.log('Created new UI camera');
  }

  /**
   * Tells the UI camera to ignore specific game objects
   * @param gameObjects Array of game objects to ignore
   */
  public setUICameraIgnore(gameObjects: Phaser.GameObjects.GameObject[]): void {
    if (this.uiCamera) {
      this.uiCamera.ignore(gameObjects);
    }
  }

  /**
   * Sets up the FPS monitor
   */
  public setupFPSMonitor(): FpsMonitor {
    // Clear any existing FPS monitors first
    this.scene.children.list.forEach(child => {
      if (child instanceof Phaser.GameObjects.Text) {
        const text = child as Phaser.GameObjects.Text;
        if (text.text.includes('FPS:')) {
          console.log('Clearing existing FPS monitor before creating a new one');
          text.destroy();
        }
      }
    });
    
    // Create the FPS monitor
    this.fpsMonitor = new FpsMonitor(this.scene);
    
    // Update the FPS display based on settings
    const showFps = this.settingsManager.getSettings().showFps;
    this.fpsMonitor.toggleDisplay(showFps);
    
    // Listen for FPS display toggle events
    this.scene.events.on('toggleFpsDisplay', (show: boolean) => {
      if (this.fpsMonitor) {
        console.log(`Scene received toggleFpsDisplay event: ${show}`);
        this.fpsMonitor.toggleDisplay(show);
      }
    });

    // Listen for FPS rate changed events
    this.scene.events.on('fpsRateChanged', (fps: number) => {
      if (this.fpsMonitor) {
        this.fpsMonitor.onFpsRateChanged(fps);
      }
    });
    
    return this.fpsMonitor;
  }

  /**
   * Toggles the FPS monitor display
   * @param show Whether to show or hide the FPS monitor
   */
  public toggleFPSMonitor(show?: boolean): void {
    if (this.fpsMonitor) {
      this.fpsMonitor.toggleDisplay(show);
    }
  }

  /**
   * Creates a progress bar with optional label
   * @param id Unique identifier for the progress bar
   * @param x X position
   * @param y Y position
   * @param label Optional label text
   * @param initialValue Initial value (0-100)
   * @returns The container holding the progress bar
   */
  public createProgressBar(id: string, x: number, y: number, label?: string, initialValue: number = 0): Phaser.GameObjects.Container {
    // Create container for progress bar elements
    const container = this.scene.add.container(x, y);
    
    // Create text label if provided
    let textLabel: Phaser.GameObjects.Text | undefined;
    let barY = 0;
    
    if (label) {
      textLabel = this.scene.add.text(0, 0, label, {
        fontSize: '18px',
        color: '#ffffff',
        stroke: '#000000',
        strokeThickness: 2,
        fontFamily: this.config.fontFamily
      });
      container.add(textLabel);
      barY = 30; // Position bar below text
    }
    
    // Create progress bar background
    const barWidth = this.config.progressBarWidth || 200;
    const barHeight = this.config.progressBarHeight || 15;
    
    const barBg = this.scene.add.rectangle(0, barY, barWidth, barHeight, this.config.progressBarBgColor || 0xffffff, 0.2);
    barBg.setOrigin(0, 0);
    
    // Create progress bar fill
    const fillWidth = (initialValue / 100) * barWidth;
    const fill = this.scene.add.rectangle(0, barY, fillWidth, barHeight, this.config.progressBarColor || 0x4CAF50, 1);
    fill.setOrigin(0, 0);
    
    // Add elements to container
    container.add([barBg, fill]);
    
    // Add to UI container
    this.uiContainer.add(container);
    
    // Store reference
    this.progressBars.set(id, {
      container,
      bar: barBg,
      fill,
      text: textLabel
    });
    
    return container;
  }
  
  /**
   * Updates the value and optionally the text of a progress bar
   * @param id Identifier of the progress bar
   * @param value New value (0-100)
   * @param text Optional new text
   */
  public updateProgressBar(id: string, value: number, text?: string): void {
    const progressBar = this.progressBars.get(id);
    if (!progressBar) return;
    
    const barWidth = this.config.progressBarWidth || 200;
    const fillWidth = Math.max(0, Math.min(100, value)) / 100 * barWidth;
    
    // Update fill width
    progressBar.fill.width = fillWidth;
    
    // Update text if provided
    if (text && progressBar.text) {
      progressBar.text.setText(text);
    }
  }
  
  /**
   * Creates a button with text and a callback
   * @param id Unique identifier for the button
   * @param x X position
   * @param y Y position
   * @param text Button text
   * @param callback Click handler
   * @param context Context for the callback
   * @param minWidth Optional minimum width for the button
   * @returns The button container
   */
  public createButton(
    id: string, 
    x: number, 
    y: number, 
    text: string, 
    callback: Function, 
    context?: any,
    minWidth?: number
  ): Phaser.GameObjects.Container {
    // Create button container
    const container = this.scene.add.container(x, y);
    
    // Create button background
    const textStyle = {
      fontSize: '16px',
      color: this.config.buttonTextColor || '#ffffff',
      fontFamily: this.config.fontFamily || 'Arial',
      padding: { x: 15, y: 8 }
    };
    
    const textObject = this.scene.add.text(0, 0, text, textStyle);
    textObject.setOrigin(0.5);
    
    // Create background based on text size
    const padding = { x: 20, y: 5 };
    const calculatedWidth = textObject.width + padding.x * 2;
    const bgWidth = minWidth ? Math.max(calculatedWidth, minWidth) : calculatedWidth;
    const bgHeight = textObject.height + padding.y * 2;
    
    const bg = this.scene.add.rectangle(0, 0, bgWidth, bgHeight, this.config.buttonBgColor || 0x4CAF50);
    bg.setOrigin(0.5);
    
    // Add elements to container (background first, then text)
    container.add([bg, textObject]);
    
    // Store the button id and text for reference
    container.setData('id', id);
    container.setData('text', textObject);
    
    // Make button interactive
    container.setInteractive(new Phaser.Geom.Rectangle(-bgWidth/2, -bgHeight/2, bgWidth, bgHeight), Phaser.Geom.Rectangle.Contains)
      .on('pointerdown', () => {
        if (context) {
          callback.call(context);
        } else {
          callback();
        }
      })
      .on('pointerover', () => {
        bg.setFillStyle(this.config.buttonHoverColor || 0x3b9c3f);
      })
      .on('pointerout', () => {
        bg.setFillStyle(this.config.buttonBgColor || 0x4CAF50);
      });
    
    // Add to UI container
    this.uiContainer.add(container);
    
    // Store reference
    this.buttons.set(id, container);
    
    return container;
  }
  
  /**
   * Creates a standard text display
   * @param id Unique identifier for the text
   * @param x X position
   * @param y Y position
   * @param text Initial text content
   * @param style Text style (optional)
   * @returns The text object
   */
  public createText(id: string, x: number, y: number, text: string, style?: Phaser.Types.GameObjects.Text.TextStyle): Phaser.GameObjects.Text {
    const defaultStyle: Phaser.Types.GameObjects.Text.TextStyle = {
      fontSize: '18px',
      color: '#ffffff',
      stroke: '#000000',
      strokeThickness: 2,
      fontFamily: this.config.fontFamily
    };
    
    const textObject = this.scene.add.text(x, y, text, { ...defaultStyle, ...style });
    
    // Add to UI container
    this.uiContainer.add(textObject);
    
    // Store reference
    this.textElements.set(id, textObject);
    
    return textObject;
  }
  
  /**
   * Updates text content
   * @param id Identifier of the text
   * @param text New text content
   */
  public updateText(id: string, text: string): void {
    const textObject = this.textElements.get(id);
    if (textObject) {
      textObject.setText(text);
    }
  }
  
  /**
   * Creates an info panel at the bottom of the screen
   * @param initialText Initial text to display
   * @returns The info panel container
   */
  public createInfoPanel(initialText: string): Phaser.GameObjects.Container {
    // Remove any existing info panel
    if (this.infoPanel) {
      this.infoPanel.destroy();
    }
    
    // Create new info panel centered at bottom of screen
    const panel = this.scene.add.container(this.scene.scale.width / 2, this.scene.scale.height - 60);
    
    // Create background
    const panelBg = this.scene.add.rectangle(
      0, 0, 
      this.config.infoPanelWidth || 600, 
      this.config.infoPanelHeight || 80, 
      this.config.infoPanelColor || 0x000000, 
      this.config.infoPanelAlpha || 0.7
    );
    panelBg.setOrigin(0.5, 0.5);
    
    // Create text
    const textObject = this.scene.add.text(0, 0, initialText, {
      fontSize: '16px',
      color: '#ffffff',
      fontFamily: this.config.fontFamily || 'Arial',
      align: 'center',
      wordWrap: { width: (this.config.infoPanelWidth || 600) - 20 }
    });
    textObject.setOrigin(0.5, 0.5);
    
    // Add elements to panel
    panel.add([panelBg, textObject]);
    
    // Add to UI container
    this.uiContainer.add(panel);
    
    // Store references
    this.infoPanel = panel;
    this.textElements.set('infoPanelText', textObject);
    
    return panel;
  }
  
  /**
   * Updates the info panel text
   * @param text New text to display
   */
  public updateInfoPanel(text: string): void {
    if (this.infoPanel) {
      const textObject = this.textElements.get('infoPanelText');
      if (textObject) {
        textObject.setText(text);
      }
    }
  }
  
  /**
   * Creates a success/completion message panel
   * @param title Title text
   * @param message Detailed message
   * @param buttonText Text for continue button
   * @param callback Function to call when continue is pressed
   * @param context Context for the callback
   * @returns The success panel container
   */
  public createSuccessPanel(
    title: string,
    message: string,
    buttonText: string,
    callback: Function,
    context?: any
  ): Phaser.GameObjects.Container {
    // Remove any existing success panel
    if (this.successPanel) {
      this.successPanel.destroy();
    }
    
    // Create centered panel
    const panel = this.scene.add.container(this.scene.scale.width / 2, this.scene.scale.height / 2);
    panel.setAlpha(0); // Start invisible for animation
    
    // Create background
    const panelBg = this.scene.add.rectangle(0, 0, 500, 300, 0x000000, 0.8);
    panelBg.setOrigin(0.5, 0.5);
    
    // Create title
    const titleText = this.scene.add.text(0, -100, title, {
      fontSize: '28px',
      color: '#ffffff',
      fontFamily: this.config.fontFamily || 'Arial',
      fontStyle: 'bold'
    });
    titleText.setOrigin(0.5, 0.5);
    
    // Create message
    const messageText = this.scene.add.text(0, -30, message, {
      fontSize: '16px',
      color: '#ffffff',
      fontFamily: this.config.fontFamily || 'Arial',
      align: 'center',
      wordWrap: { width: 460 }
    });
    messageText.setOrigin(0.5, 0.5);
    
    // Create continue button
    const buttonContainer = this.scene.add.container(0, 80);
    
    const buttonTextObj = this.scene.add.text(0, 0, buttonText, {
      fontSize: '16px',
      color: '#ffffff',
      fontFamily: this.config.fontFamily || 'Arial',
      padding: { x: 15, y: 8 }
    });
    buttonTextObj.setOrigin(0.5);
    
    const buttonBg = this.scene.add.rectangle(
      0, 0,
      buttonTextObj.width + 40,
      buttonTextObj.height + 20,
      this.config.buttonBgColor || 0x4CAF50
    );
    buttonBg.setOrigin(0.5);
    
    buttonContainer.add([buttonBg, buttonTextObj]);
    
    // Make button interactive
    buttonContainer.setInteractive(
      new Phaser.Geom.Rectangle(
        -buttonBg.width/2,
        -buttonBg.height/2,
        buttonBg.width,
        buttonBg.height
      ),
      Phaser.Geom.Rectangle.Contains
    )
    .on('pointerdown', () => {
      if (context) {
        callback.call(context);
      } else {
        callback();
      }
    })
    .on('pointerover', () => {
      buttonBg.setFillStyle(this.config.buttonHoverColor || 0x3b9c3f);
    })
    .on('pointerout', () => {
      buttonBg.setFillStyle(this.config.buttonBgColor || 0x4CAF50);
    });
    
    // Add all elements to panel
    panel.add([panelBg, titleText, messageText, buttonContainer]);
    
    // Add to UI container
    this.uiContainer.add(panel);
    
    // Store reference
    this.successPanel = panel;
    
    return panel;
  }
  
  /**
   * Shows the success panel with animation
   */
  public showSuccessPanel(): void {
    if (this.successPanel) {
      // Hide info panel if it exists
      if (this.infoPanel) {
        this.scene.tweens.add({
          targets: this.infoPanel,
          alpha: 0,
          duration: 500,
          ease: 'Power2'
        });
      }
      
      // Show success panel with animation
      this.scene.tweens.add({
        targets: this.successPanel,
        alpha: 1,
        duration: 1000,
        ease: 'Power2'
      });
    }
  }
  
  /**
   * Shows a notification message at the top of the screen
   * @param message Message to display
   * @param duration Duration in ms to show the message (0 for permanent)
   */
  public showNotification(message: string, duration: number = 3000): void {
    // Remove any existing notification
    this.hideNotification();
    
    // Create text object
    const notification = this.scene.add.text(this.scene.scale.width / 2, 50, message, {
      fontSize: '18px',
      fontFamily: this.config.fontFamily || 'Arial',
      color: '#ffffff',
      stroke: '#000000',
      strokeThickness: 4,
      padding: { x: 20, y: 10 },
      backgroundColor: '#333333BB'
    });
    
    notification.setOrigin(0.5, 0);
    notification.setDepth(this.config.containerDepth! + 1);
    notification.setScrollFactor(0);
    
    // Add to UI container
    this.uiContainer.add(notification);
    
    // Store reference
    this.textElements.set('notification', notification);
    
    // Auto-hide after duration (if not permanent)
    if (duration > 0) {
      this.scene.time.delayedCall(duration, () => this.hideNotification());
    }
  }
  
  /**
   * Hides the current notification if one exists
   */
  public hideNotification(): void {
    const notification = this.textElements.get('notification');
    if (notification) {
      notification.destroy();
      this.textElements.delete('notification');
    }
  }
  
  /**
   * Creates a back button (Menu)
   * @param callback Function to call when back is pressed
   * @param context Context for the callback
   * @returns The back button text object
   */
  public createBackButton(callback: Function, context?: any): Phaser.GameObjects.Text {
    const backButton = this.scene.add.text(this.scene.scale.width - 20, 20, 'Menu', {
      fontSize: '18px',
      color: '#ffffff',
      backgroundColor: '#1a6dd7',
      padding: { x: 10, y: 5 },
      stroke: '#000000',
      strokeThickness: 1
    })
    .setOrigin(1, 0)
    .setInteractive({ useHandCursor: true })
    .on('pointerdown', () => {
      if (context) {
        callback.call(context);
      } else {
        callback();
      }
    });
    
    // Add to UI container
    this.uiContainer.add(backButton);
    
    // Store reference
    this.textElements.set('backButton', backButton);
    
    return backButton;
  }
  
  /**
   * Creates a fullscreen button
   * @param x X position
   * @param y Y position
   * @returns The created fullscreen button
   */
  public createFullscreenButton(x: number = 20, y: number = 60): Phaser.GameObjects.Image {
    // Remove any existing fullscreen button
    if (this.fullscreenButton) {
      this.fullscreenButton.destroy();
    }
    
    // Create fullscreen icon if it doesn't exist
    if (!this.scene.textures.exists('fullscreen-icon')) {
      const fsGraphics = this.scene.add.graphics();
      fsGraphics.fillStyle(0xFFFFFF, 1);
      fsGraphics.fillRect(0, 0, 16, 16);
      fsGraphics.fillStyle(0x000000, 1);
      fsGraphics.fillRect(4, 4, 8, 8);
      fsGraphics.generateTexture('fullscreen-icon', 16, 16);
      fsGraphics.destroy();
    }
    
    // Add fullscreen button to the top-right corner
    this.fullscreenButton = this.scene.add.image(
      this.scene.scale.width - x, 
      y,
      'fullscreen-icon'
    )
    .setOrigin(1, 0)
    .setScale(2)
    .setInteractive({ useHandCursor: true })
    .on('pointerdown', () => {
      if (this.scene.scale.isFullscreen) {
        this.scene.scale.stopFullscreen();
      } else {
        this.scene.scale.startFullscreen();
      }
    });
    
    // Add to UI container
    this.uiContainer.add(this.fullscreenButton);
    
    // Store reference with data for repositioning on resize
    this.fullscreenButton.setData('alignRight', true);
    this.fullscreenButton.setData('rightMargin', x);
    
    return this.fullscreenButton;
  }
  
  /*  
   * Updates the UI components with provided data
   * TODO: switch to event-based UI updates, this will update the UI only when needed
   * Instead of polling for changes every frame, an event-driven approach would:
   * 1. Have the Cell emit events when energy or health changes
   * 2. Have the UI components subscribe to these events
   * 3. Update only when needed, reducing unnecessary updates
   * This creates a cleaner separation of concerns where:
   * - The game objects (like Cell) just manage their state and emit events
   * - The UI system listens for these events and updates accordingly
   * - GameScene doesn't need to mediate this relationship in its update loop
   * @param componentUpdates Object with component IDs as keys and update data as values
   * @param delta Delta time from the last update (for animations)
   */

  public updateUI(componentUpdates: Record<string, any> = {}, delta: number = 0): void {
    // Update registered components with their data
    Object.entries(componentUpdates).forEach(([id, data]) => {
      const component = this.registeredComponents.get(id);
      if (component) {
        component.update(data, delta);
      } else if (this.progressBars.has(id)) {
        // Handle standard progress bar updates
        if (typeof data === 'number') {
          this.updateProgressBar(id, data);
        } else if (data && typeof data === 'object' && 'value' in data) {
          const value = data.value as number;
          const text = data.text as string | undefined;
          this.updateProgressBar(id, value, text);
        }
      } else if (this.textElements.has(id)) {
        // Handle standard text updates
        if (typeof data === 'string') {
          this.updateText(id, data);
        }
      }
    });
  }

  /**
   * Register a component for updates
   * @param component Component object with ID and update function
   */
  public registerComponent(component: UIComponent): void {
    this.registeredComponents.set(component.id, component);
  }

  /**
   * Unregister a component
   * @param id ID of the component to unregister
   */
  public unregisterComponent(id: string): void {
    this.registeredComponents.delete(id);
  }

  /**
   * Updates UI positions when the window is resized
   */
  public handleResize(): void {
    // Prevent recursive calls by checking if we're already handling a resize
    if (this._handlingResize) return;
    this._handlingResize = true;
    
    // Update info panel position
    if (this.infoPanel) {
      this.infoPanel.setPosition(this.scene.scale.width / 2, this.scene.scale.height - 60);
    }
    
    // Update success panel position
    if (this.successPanel) {
      this.successPanel.setPosition(this.scene.scale.width / 2, this.scene.scale.height / 2);
    }
    
    // Update back button position
    const backButton = this.textElements.get('backButton');
    if (backButton) {
      backButton.setPosition(this.scene.scale.width - 20, 20);
    }
    
    // Update fullscreen button position
    if (this.fullscreenButton) {
      const rightMargin = this.fullscreenButton.getData('rightMargin') || 20;
      this.fullscreenButton.setPosition(this.scene.scale.width - rightMargin, this.fullscreenButton.y);
    }

    // Update UI camera size if it exists
    if (this.uiCamera) {
      this.uiCamera.setSize(this.scene.scale.width, this.scene.scale.height);
      console.log('Updated UI camera size to:', this.scene.scale.width, this.scene.scale.height);
    }
    
    // Update FPS monitor position if it exists
    if (this.fpsMonitor) {
      this.fpsMonitor.handleResize();
    }
    
    // Update any elements with alignment data
    this.uiContainer.iterate((child: Phaser.GameObjects.GameObject) => {
      // Reposition any elements that should be aligned to screen edges
      if (child.getData('alignRight')) {
        // Cast to a component type that has setX method
        const component = child as unknown as { setX: (x: number) => void };
        component.setX(this.scene.scale.width - child.getData('rightMargin'));
      }
      if (child.getData('alignBottom')) {
        // Cast to a component type that has setY method
        const component = child as unknown as { setY: (y: number) => void };
        component.setY(this.scene.scale.height - child.getData('bottomMargin'));
      }
      return true;
    });
    
    // Clear the resize handling flag
    this._handlingResize = false;
  }
  
  /**
   * Gets a progress bar by ID
   * @param id The ID of the progress bar
   * @returns The progress bar data or undefined if not found
   */
  public getProgressBar(id: string): {
    container: Phaser.GameObjects.Container;
    bar: Phaser.GameObjects.Rectangle;
    fill: Phaser.GameObjects.Rectangle;
    text?: Phaser.GameObjects.Text;
  } | undefined {
    return this.progressBars.get(id);
  }
  
  /**
   * Gets the UI container
   * @returns The container containing all UI elements
   */
  public getContainer(): Phaser.GameObjects.Container {
    return this.uiContainer;
  }

  /**
   * Clean up resources on scene shutdown
   */
  public destroy(): void {
    // Clean up FPS monitor
    if (this.fpsMonitor) {
      this.fpsMonitor.destroy();
      this.fpsMonitor = null;
    }
    
    // Remove event listeners
    this.scene.scale.off('resize', this.handleResize, this);
    this.scene.events.off('toggleFpsDisplay');
    this.scene.events.off('fpsRateChanged');
    
    // Clear registered components
    this.registeredComponents.clear();
    
    // Note: We don't destroy the UI container or its children here
    // as they might be managed by the scene itself
  }
} 